//@flow
import React, { useEffect, useState } from 'react';
import Snackbar from '@material-ui/core/Snackbar';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import { makeStyles } from '@material-ui/core/styles';
import { useStore } from '../../store/useStore';
import { TypeAppVerDismissed } from '../../store/appVersion';

const useStyles = makeStyles((theme) => ({
  snackbar: {
    top: theme.spacing(10), // Below the AppHeader
  },
  content: {
    backgroundColor: theme.palette.secondary.main,
    color: theme.palette.secondary.contrastText,
    padding: theme.spacing(1.5, 2),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[8],
    minWidth: 400,
    [theme.breakpoints.down('sm')]: {
      minWidth: 'auto',
      maxWidth: '90vw',
    },
  },
  message: {
    flex: 1,
    fontSize: '0.95rem',
    fontWeight: 500,
  },
  versionBadge: {
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    padding: theme.spacing(0.5, 1),
    borderRadius: theme.shape.borderRadius,
    fontSize: '0.85rem',
    fontWeight: 600,
    marginLeft: theme.spacing(1),
  },
  actions: {
    display: 'flex',
    gap: theme.spacing(1),
  },
  updateButton: {
    backgroundColor: theme.palette.common.white,
    color: theme.palette.secondary.main,
    fontWeight: 600,
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.9)',
    },
  },
  laterButton: {
    color: theme.palette.common.white,
    borderColor: 'rgba(255, 255, 255, 0.5)',
    '&:hover': {
      borderColor: theme.palette.common.white,
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
    },
  },
}));

const DISMISS_DURATION = 30 * 60 * 1000; // 30 minutes

export default function UpdateNotification(): React$Node {
  const classes = useStyles();
  const { dispatch, state } = useStore();
  const [open, setOpen] = useState(false);
  
  const appUpdate = state.appVer?.appUpdate;
  const appVersion = state.appVer?.appVersion;
  const dismissedUntil = state.appVer?.dismissedUntil;
  
  useEffect(() => {
    // Show notification if update is available and not dismissed
    if (appUpdate) {
      const now = Date.now();
      
      // Check if dismissal period has expired
      if (!dismissedUntil || now > dismissedUntil) {
        setOpen(true);
      } else {
        setOpen(false);
      }
    } else {
      setOpen(false);
    }
  }, [appUpdate, dismissedUntil]);
  
  const handleUpdateNow = () => {
    // Force hard reload to get latest version
    window.location.reload(true);
  };
  
  const handleUpdateLater = () => {
    const dismissUntil = Date.now() + DISMISS_DURATION;
    dispatch({ 
      type: TypeAppVerDismissed, 
      dismissedUntil: dismissUntil 
    });
    setOpen(false);
  };
  
  const handleClose = () => {
    handleUpdateLater();
  };
  
  return (
    <Snackbar
      className={classes.snackbar}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'center',
      }}
      open={open}
      onClose={handleClose}
    >
      <div className={classes.content}>
        <div className={classes.message}>
          🎉 New version available
          <span className={classes.versionBadge}>v{appVersion}</span>
        </div>
        <div className={classes.actions}>
          <Button
            size="small"
            variant="contained"
            className={classes.updateButton}
            onClick={handleUpdateNow}
          >
            Update Now
          </Button>
          <Button
            size="small"
            variant="outlined"
            className={classes.laterButton}
            onClick={handleUpdateLater}
          >
            Later
          </Button>
          <IconButton 
            size="small" 
            onClick={handleClose}
            style={{ color: 'white' }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>
      </div>
    </Snackbar>
  );
}
